import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageBody, PageHero } from '../../components/layout';
import { EconomicsFigures } from './components/EconomicsFigures';
import { useGenerateLandscapeReport, useLandscapeReports } from './hooks/useLandscapeReports';
import { useLandscape } from './hooks/useLandscapes';
const TEMPLATES = [
    {
        id: 'review-brief-v1',
        label: 'Review brief'
    },
    {
        id: 'economics-only-v1',
        label: 'Economics only'
    }
];
const today = ()=>new Date().toISOString().slice(0, 10);
export function LandscapeReportsPage() {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const landscape = useLandscape(id);
    const reports = useLandscapeReports(id);
    const generate = useGenerateLandscapeReport(id);
    const [templateId, setTemplateId] = useState(TEMPLATES[0].id);
    const [asOf, setAsOf] = useState(today());
    const [windowDays, setWindowDays] = useState(30);
    const [preview, setPreview] = useState(null);
    const economicsOnly = templateId === 'economics-only-v1';
    if (reports.isLoading) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Loading…"
            })
        });
    }
    if (reports.error) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                "data-testid": Testids.landscapeReports.unavailable,
                children: "Review briefs are unavailable to you or could not be loaded."
            })
        });
    }
    const rows = reports.data?.reports ?? [];
    const content = preview?.version?.content;
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.landscapeReports.page,
        children: /*#__PURE__*/ _jsxs(PageBody, {
            children: [
                /*#__PURE__*/ _jsx(PageHero, {
                    title: "Review briefs",
                    subtitle: landscape.data?.name,
                    actions: /*#__PURE__*/ _jsx(Link, {
                        to: `/landscapes/${id}`,
                        className: "text-sm text-muted-foreground underline",
                        "data-testid": Testids.landscapeReports.backLink,
                        children: "Back to Landscape"
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-6",
                    children: [
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-3 rounded-md border border-border p-3",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-sm font-medium",
                                    children: "Generate a review brief"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap items-end gap-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "Template",
                                            children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                                size: "sm",
                                                value: templateId,
                                                onChange: (e)=>{
                                                    setTemplateId(e.target.value);
                                                    setPreview(null);
                                                },
                                                "data-testid": Testids.landscapeReports.templateSelect,
                                                children: TEMPLATES.map((t)=>/*#__PURE__*/ _jsx("option", {
                                                        value: t.id,
                                                        children: t.label
                                                    }, t.id))
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "As of",
                                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                                type: "date",
                                                value: asOf,
                                                onChange: (e)=>{
                                                    setAsOf(e.target.value);
                                                    setPreview(null);
                                                },
                                                "data-testid": Testids.landscapeReports.asOfInput
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraFormField, {
                                            label: "Changes in the last N days",
                                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                                type: "number",
                                                min: 0,
                                                disabled: economicsOnly,
                                                title: economicsOnly ? 'Not applicable to Economics only' : undefined,
                                                value: windowDays,
                                                onChange: (e)=>{
                                                    setWindowDays(Number(e.target.value));
                                                    setPreview(null);
                                                },
                                                "data-testid": Testids.landscapeReports.windowInput
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "outline",
                                            size: "sm",
                                            disabled: generate.isPending,
                                            "data-testid": Testids.landscapeReports.previewButton,
                                            onClick: ()=>generate.mutate({
                                                    templateId,
                                                    asOf,
                                                    windowDays,
                                                    preview: true
                                                }, {
                                                    onSuccess: (data)=>setPreview(data)
                                                }),
                                            children: "Preview"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            disabled: generate.isPending,
                                            "data-testid": Testids.landscapeReports.generateButton,
                                            onClick: ()=>generate.mutate({
                                                    templateId,
                                                    asOf,
                                                    windowDays,
                                                    preview: false
                                                }, {
                                                    onSuccess: (data)=>{
                                                        const v = data.version;
                                                        if (v) navigate(`/landscapes/${id}/briefs/${v.reportId}`);
                                                    }
                                                }),
                                            children: "Generate brief"
                                        })
                                    ]
                                }),
                                content && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 rounded-md border border-dashed border-border p-3",
                                    "data-testid": Testids.landscapeReports.preview,
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm font-medium",
                                            children: "Nothing is saved until you generate."
                                        }),
                                        /*#__PURE__*/ _jsxs("dl", {
                                            className: "grid grid-cols-2 gap-2 text-sm sm:grid-cols-4",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("dt", {
                                                            className: "text-xs text-muted-foreground",
                                                            children: "Scope"
                                                        }),
                                                        /*#__PURE__*/ _jsx("dd", {
                                                            children: content.scope?.kind === 'landscape' ? content.scope.landscapeName : 'Portfolio'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("dt", {
                                                            className: "text-xs text-muted-foreground",
                                                            children: "As of"
                                                        }),
                                                        /*#__PURE__*/ _jsx("dd", {
                                                            children: content.asOf
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("dt", {
                                                            className: "text-xs text-muted-foreground",
                                                            children: "Template"
                                                        }),
                                                        /*#__PURE__*/ _jsx("dd", {
                                                            children: content.templateId
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("dt", {
                                                            className: "text-xs text-muted-foreground",
                                                            children: "Opportunities in scope"
                                                        }),
                                                        /*#__PURE__*/ _jsx("dd", {
                                                            children: content.opportunitiesInScope.toString()
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-sm",
                                            "data-testid": Testids.landscapeReports.missingData,
                                            children: [
                                                content.opportunitiesMissingValues.toString(),
                                                " of",
                                                ' ',
                                                content.opportunitiesInScope.toString(),
                                                " opportunities in scope carry no recorded value"
                                            ]
                                        }),
                                        content.figures && /*#__PURE__*/ _jsx(EconomicsFigures, {
                                            figures: content.figures
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-sm font-medium",
                                    children: "History"
                                }),
                                rows.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    "data-testid": Testids.landscapeReports.listEmpty,
                                    children: "No review briefs yet. Generate one to freeze today's figures."
                                }) : /*#__PURE__*/ _jsx("ul", {
                                    className: "divide-y divide-border",
                                    children: rows.map((r)=>/*#__PURE__*/ _jsx("li", {
                                            children: /*#__PURE__*/ _jsxs(Link, {
                                                to: `/landscapes/${id}/briefs/${r.reportId}`,
                                                className: "flex flex-wrap items-center justify-between gap-2 py-2 text-sm",
                                                "data-testid": Testids.landscapeReports.listRow,
                                                "data-report-id": r.reportId,
                                                "data-version": r.latestVersion,
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        children: r.title || r.templateId
                                                    }),
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        className: "text-muted-foreground",
                                                        children: [
                                                            r.templateId,
                                                            " · as of ",
                                                            r.asOf,
                                                            " · v",
                                                            r.latestVersion,
                                                            " · ",
                                                            r.createdAt
                                                        ]
                                                    })
                                                ]
                                            })
                                        }, r.reportId))
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
